import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Loader2, LogOut, Users, FileCheck2, Mail, Eye, HeartHandshake, TrendingUp, Copy } from "lucide-react";
import { toast } from "sonner";
import { WeaveLogo } from "@/components/WeaveLogo";
import { adminLogin, adminOverview, adminLeads, markInterestBooked, updateClarityOutcome, downloadCsv, adminPayments, adminUsers, createAdminUser, resetAdminPassword, refundPayment } from "@/lib/api";

const TOKEN_KEY = "weave_admin_token";

const CTA_LABELS = {
  clarity_conversation: "Clarity Consult",
  taster_session: "Legacy CTA",
  register_interest: "Cohort interest",
  prospectus_download: "Prospectus download",
};

const CHANGE_MESSAGE = "Change campaign landing page";

const PLAN_LABELS = {
  clarity: "Personal Clarity",
  clarity_balance: "Completion balance",
  standard: "Full year",
  deposit: "Legacy deposit",
  full: "Legacy full payment",
  balance: "Legacy balance",
  monthly: "Monthly instalments",
};

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

const LoginCard = ({ onSuccess }) => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await adminLogin(form.email, form.password);
      sessionStorage.setItem(TOKEN_KEY, res.token);
      onSuccess(res.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login">
      <div className="w-full max-w-sm bg-white border border-weave-line rounded-2xl p-8">
        <WeaveLogo height={26} />
        <h1 className="mt-6 text-2xl font-medium text-weave-ink">Admin sign in</h1>
        <p className="mt-1 text-sm text-weave-slate">Founder Capacity Index dashboard</p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <input
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="admin-email-input"
            className="w-full rounded-xl border border-weave-line bg-white px-4 py-3 text-sm text-weave-ink placeholder:text-weave-mute focus:border-weave-teal focus:outline-none"
          />
          <input
            type="password"
            placeholder="Password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="admin-password-input"
            className="w-full rounded-xl border border-weave-line bg-white px-4 py-3 text-sm text-weave-ink placeholder:text-weave-mute focus:border-weave-teal focus:outline-none"
          />
          {error && <p className="text-sm text-weave-red" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-button"
            className="w-full inline-flex items-center justify-center gap-2 bg-weave-teal text-white hover:bg-weave-tealdeep transition-colors duration-300 rounded-full px-8 py-3.5 font-medium disabled:opacity-60"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : null}
            Sign in
          </button>
        </form>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="bg-white border border-weave-line rounded-2xl p-6" data-testid={testId}>
    <div className="flex items-center gap-2 text-weave-teal">
      <Icon size={17} />
      <span className="text-xs font-bold uppercase tracking-[0.15em]">{label}</span>
    </div>
    <p className="mt-3 text-3xl font-light text-weave-ink">{value}</p>
  </div>
);

export default function AdminDashboard() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [overview, setOverview] = useState(null);
  const [leads, setLeads] = useState(null);
  const [payments, setPayments] = useState(null);
  const [adminUserList, setAdminUserList] = useState([]);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [tempAdminCredential, setTempAdminCredential] = useState(null);
  const [leadFilter, setLeadFilter] = useState("all");
  const [outcomeFilter, setOutcomeFilter] = useState("all");
  const [loading, setLoading] = useState(!!token);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const [ov, ld, pm, au] = await Promise.all([adminOverview(t), adminLeads(t), adminPayments(t), adminUsers(t)]);
      setOverview(ov);
      setLeads(ld);
      setPayments(pm.payments);
      setAdminUserList(au.users);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast.error("Session expired — please sign in again.");
      } else {
        toast.error("Could not load the dashboard.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setOverview(null);
    setLeads(null);
  };

  const handleRefund = async (p) => {
    const labels = {
      clarity: "£499",
      clarity_balance: "£2,501",
      standard: "£3,000",
      deposit: "£300",
      full: "£2,600",
      balance: "£2,300",
      monthly: "the first £300 monthly payment",
    };
    const label = labels[p.plan] || (p.amount ? `£${p.amount.toLocaleString()}` : "this payment");
    if (!window.confirm(`Refund ${label} to ${p.email || "this buyer"}? This cannot be undone.`)) return;
    try {
      await refundPayment(p.session_id, token);
      toast.success("Refund issued — Stripe will return the money to their card.");
      load(token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refund failed — please try again.");
    }
  };

  const toggleBooked = async (interest) => {
    try {
      await markInterestBooked(interest.id, !interest.booked, token);
      setLeads({
        ...leads,
        interests: leads.interests.map((i) => (i.id === interest.id ? { ...i, booked: !i.booked } : i)),
      });
      toast.success(!interest.booked ? "Marked as booked." : "Booking mark removed.");
    } catch {
      toast.error("Could not update — please try again.");
    }
  };

  const handleOutcome = async (interest, outcome) => {
    try {
      await updateClarityOutcome(interest.id, outcome, token);
      setLeads({
        ...leads,
        interests: leads.interests.map((i) => (i.id === interest.id ? { ...i, clarity_outcome: outcome } : i)),
      });
      toast.success("Clarity outcome updated.");
    } catch {
      toast.error("Could not update outcome — please try again.");
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    if (!newAdminEmail) {
      toast.error("Add the team member's email address.");
      return;
    }
    try {
      const created = await createAdminUser(newAdminEmail, token);
      setTempAdminCredential(created);
      setNewAdminEmail("");
      const refreshed = await adminUsers(token);
      setAdminUserList(refreshed.users);
      toast.success("Admin account created.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create admin.");
    }
  };

  const handleResetAdminPassword = async (user) => {
    if (!window.confirm(`Reset the password for ${user.email}? The old password will stop working.`)) return;
    try {
      const updated = await resetAdminPassword(user.id, token);
      setTempAdminCredential(updated);
      const refreshed = await adminUsers(token);
      setAdminUserList(refreshed.users);
      toast.success("Temporary password generated.");
    } catch {
      toast.error("Could not reset password.");
    }
  };

  const copyTempAdminPassword = async () => {
    if (!tempAdminCredential?.temporary_password) return;
    await navigator.clipboard.writeText(tempAdminCredential.temporary_password);
    toast.success("Temporary password copied.");
  };

  if (!token) return <LoginCard onSuccess={setToken} />;

  if (loading || !overview || !leads) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-weave-slate" data-testid="admin-loading">
        <Loader2 className="animate-spin" size={28} />
        <p className="text-sm">Loading dashboard…</p>
      </div>
    );
  }

  const completionRate = overview.assessments_started
    ? Math.round((overview.assessments_completed / overview.assessments_started) * 100)
    : 0;
  let filteredInterests = leads.interests;
  if (leadFilter === "change") {
    filteredInterests = filteredInterests.filter((i) => i.message === CHANGE_MESSAGE);
  } else if (leadFilter === "prospectus") {
    filteredInterests = filteredInterests.filter((i) => i.type === "prospectus_download");
  }
  if (outcomeFilter !== "all") {
    filteredInterests = filteredInterests.filter((i) => (i.clarity_outcome || "undecided") === outcomeFilter);
  }
  const exportParams = new URLSearchParams();
  if (leadFilter !== "all") exportParams.set("source", leadFilter);
  if (outcomeFilter !== "all") exportParams.set("outcome", outcomeFilter);
  const interestsExportPath = `interests.csv${exportParams.toString() ? `?${exportParams.toString()}` : ""}`;
  const interestsExportName = `weave-${leadFilter === "change" ? "change-campaign-" : leadFilter === "prospectus" ? "prospectus-" : ""}${outcomeFilter !== "all" ? `${outcomeFilter}-` : ""}leads.csv`;

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-weave-line bg-weave-cream/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" aria-label="Back to site"><WeaveLogo height={26} /></Link>
          <div className="flex items-center gap-4">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-weave-mute hidden sm:block">Admin</span>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 text-sm text-weave-slate hover:text-weave-ink transition-colors duration-200"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <h1 className="text-3xl md:text-4xl font-light tracking-tight text-weave-ink" data-testid="admin-heading">
          Leads &amp; demand
        </h1>
        <p className="mt-2 text-sm text-weave-slate">Every assessment started, every hand raised, every CTA chosen.</p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-10" data-testid="admin-stats">
          <StatCard icon={TrendingUp} label="Started" value={overview.assessments_started} testId="stat-started" />
          <StatCard icon={FileCheck2} label="Completed" value={`${overview.assessments_completed} · ${completionRate}%`} testId="stat-completed" />
          <StatCard icon={Eye} label="Reports viewed" value={overview.reports_viewed} testId="stat-viewed" />
          <StatCard icon={Mail} label="Reports emailed" value={overview.reports_emailed} testId="stat-emailed" />
          <StatCard icon={Users} label="CTA leads" value={overview.leads_total} testId="stat-leads" />
          <StatCard icon={HeartHandshake} label="Consent to contact" value={overview.marketing_consents} testId="stat-consents" />
        </div>

        <div className="flex flex-wrap gap-2 mt-6" data-testid="leads-by-type">
          {Object.entries(overview.leads_by_type).map(([type, count]) => (
            <span key={type} className="text-xs bg-white border border-weave-line rounded-full px-4 py-2 text-weave-slate">
              {CTA_LABELS[type] || type}: <strong className="text-weave-ink">{count}</strong>
            </span>
          ))}
        </div>

        <section className="mt-10 bg-white border border-weave-line rounded-2xl p-6" data-testid="admin-users-section">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div>
              <h2 className="text-xl md:text-2xl font-medium text-weave-ink">Team admin access</h2>
              <p className="mt-2 text-sm text-weave-slate max-w-2xl">
                Create a named admin login for each team member. A temporary password is shown once;
                share it securely and use Reset if it is lost.
              </p>
            </div>
            <form onSubmit={handleCreateAdmin} className="flex flex-col sm:flex-row gap-3" data-testid="add-admin-form">
              <input
                type="email"
                aria-label="New admin email"
                placeholder="team@wearetheweave.co.uk"
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                data-testid="new-admin-email-input"
                className="rounded-full border border-weave-line bg-white px-4 py-2.5 text-sm text-weave-ink placeholder:text-weave-mute focus:border-weave-teal focus:outline-none min-w-[260px]"
              />
              <button
                type="submit"
                data-testid="add-admin-button"
                className="inline-flex items-center justify-center gap-2 text-sm font-medium text-white bg-weave-teal hover:bg-[#3f8f93] transition-colors duration-200 rounded-full px-5 py-2.5"
              >
                Add admin
              </button>
            </form>
          </div>
          {tempAdminCredential && (
            <div className="mt-5 rounded-xl border border-weave-yellow/40 bg-weave-yellow/10 p-4" data-testid="temp-admin-credential">
              <p className="text-sm font-medium text-weave-ink">
                {tempAdminCredential.email}: <span className="font-mono">{tempAdminCredential.temporary_password}</span>
              </p>
              <button
                type="button"
                onClick={copyTempAdminPassword}
                data-testid="copy-temp-admin-password-button"
                className="mt-3 inline-flex items-center gap-2 text-xs font-medium text-weave-teal border border-weave-teal/40 hover:bg-weave-teal/10 transition-colors duration-200 rounded-full px-4 py-2"
              >
                <Copy size={13} /> Copy temporary password
              </button>
              <p className="mt-1 text-xs text-weave-slate">This will not be shown again after you leave or reload.</p>
            </div>
          )}
          <div className="mt-5 divide-y divide-weave-line" data-testid="admin-users-list">
            {adminUserList.map((user) => (
              <div key={user.id} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={`admin-user-${user.email}`}>
                <div>
                  <p className="text-sm font-medium text-weave-ink">{user.email}</p>
                  <p className="text-xs text-weave-mute">Created {formatDate(user.created_at)}{user.password_reset_at ? ` · password reset ${formatDate(user.password_reset_at)}` : ""}</p>
                </div>
                <button
                  onClick={() => handleResetAdminPassword(user)}
                  data-testid={`reset-admin-password-${user.id.slice(0, 8)}`}
                  className="inline-flex items-center justify-center text-xs font-medium text-weave-teal border border-weave-teal/40 hover:bg-weave-teal/10 transition-colors duration-200 rounded-full px-4 py-2"
                >
                  Reset password
                </button>
              </div>
            ))}
          </div>
        </section>


        <section className="mt-14" data-testid="assessments-table-section">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl md:text-2xl font-medium text-weave-ink">Assessments</h2>
            <button
              onClick={() => downloadCsv("assessments.csv", "weave-assessments.csv", token)}
              data-testid="export-assessments-button"
              className="inline-flex items-center gap-2 text-sm font-medium text-weave-teal border border-weave-teal/40 hover:bg-weave-teal/10 transition-colors duration-200 rounded-full px-5 py-2.5"
            >
              <Download size={15} /> Export CSV
            </button>
          </div>
          <div className="mt-5 bg-white border border-weave-line rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]" data-testid="assessments-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-[0.12em] text-weave-mute border-b border-weave-line">
                  <th className="px-5 py-4 font-medium">Person</th>
                  <th className="px-5 py-4 font-medium">Completed</th>
                  <th className="px-5 py-4 font-medium">Overall</th>
                  <th className="px-5 py-4 font-medium">Profile</th>
                  <th className="px-5 py-4 font-medium">Strengths</th>
                  <th className="px-5 py-4 font-medium">Focus</th>
                  <th className="px-5 py-4 font-medium">Consent</th>
                  <th className="px-5 py-4 font-medium">CTAs</th>
                </tr>
              </thead>
              <tbody>
                {leads.assessments.length === 0 && (
                  <tr><td colSpan={8} className="px-5 py-8 text-center text-weave-mute">No assessments yet.</td></tr>
                )}
                {leads.assessments.map((p) => (
                  <tr key={p.id} className="border-b border-weave-line last:border-0 hover:bg-weave-cream/60 transition-colors duration-150" data-testid={`assessment-row-${p.id.slice(0, 8)}`}>
                    <td className="px-5 py-4">
                      <p className="font-medium text-weave-ink">{p.first_name || "—"}</p>
                      <p className="text-xs text-weave-mute">{p.email || "no email"}</p>
                    </td>
                    <td className="px-5 py-4 text-weave-slate">{p.completed_at ? formatDate(p.completed_at) : <span className="text-weave-mute">in progress</span>}</td>
                    <td className="px-5 py-4 font-medium text-weave-ink">{p.overall ?? "—"}</td>
                    <td className="px-5 py-4 text-weave-slate">{p.profile || "—"}</td>
                    <td className="px-5 py-4 text-weave-slate">{p.strengths.join(", ") || "—"}</td>
                    <td className="px-5 py-4 text-weave-slate">{p.priorities.join(", ") || "—"}</td>
                    <td className="px-5 py-4">
                      <span className={`text-xs font-medium rounded-full px-3 py-1 ${p.marketing_consent ? "bg-weave-teal/10 text-weave-teal" : "bg-weave-sand text-weave-mute"}`}>
                        {p.marketing_consent ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-weave-slate text-xs">
                      {p.ctas.length ? p.ctas.map((c) => CTA_LABELS[c] || c).join(", ") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-14" data-testid="interests-table-section">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-xl md:text-2xl font-medium text-weave-ink">CTA leads</h2>
              <p className="mt-1 text-xs text-weave-mute" data-testid="lead-filter-count">
                Showing {filteredInterests.length} of {leads.interests.length} leads
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={leadFilter}
                onChange={(e) => setLeadFilter(e.target.value)}
                data-testid="lead-source-filter"
                className="rounded-full border border-weave-line bg-white px-4 py-2.5 text-sm text-weave-slate focus:border-weave-teal focus:outline-none"
              >
                <option value="all">All lead sources</option>
                <option value="change">Change campaign</option>
                <option value="prospectus">Prospectus downloads</option>
              </select>
              <select
                value={outcomeFilter}
                onChange={(e) => setOutcomeFilter(e.target.value)}
                data-testid="clarity-outcome-filter"
                className="rounded-full border border-weave-line bg-white px-4 py-2.5 text-sm text-weave-slate focus:border-weave-teal focus:outline-none"
              >
                <option value="all">All clarity outcomes</option>
                <option value="undecided">Still deciding</option>
                <option value="continuing">Continuing</option>
                <option value="declined">Declined</option>
              </select>
              <button
                onClick={() => downloadCsv(interestsExportPath, interestsExportName, token)}
                data-testid="export-interests-button"
                className="inline-flex items-center gap-2 text-sm font-medium text-weave-teal border border-weave-teal/40 hover:bg-weave-teal/10 transition-colors duration-200 rounded-full px-5 py-2.5"
              >
                <Download size={15} /> Export CSV
              </button>
            </div>
          </div>
          <div className="mt-5 bg-white border border-weave-line rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[980px]" data-testid="interests-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-[0.12em] text-weave-mute border-b border-weave-line">
                  <th className="px-5 py-4 font-medium">Type</th>
                  <th className="px-5 py-4 font-medium">Person</th>
                  <th className="px-5 py-4 font-medium">Message</th>
                  <th className="px-5 py-4 font-medium">Date</th>
                  <th className="px-5 py-4 font-medium">Follow-up</th>
                  <th className="px-5 py-4 font-medium">Clarity outcome</th>
                  <th className="px-5 py-4 font-medium">Booked</th>
                </tr>
              </thead>
              <tbody>
                {filteredInterests.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-8 text-center text-weave-mute">
                    {leadFilter === "change" ? "No Change campaign leads yet." : "No leads yet."}
                  </td></tr>
                )}
                {filteredInterests.map((i) => (
                  <tr key={i.id} className="border-b border-weave-line last:border-0 hover:bg-weave-cream/60 transition-colors duration-150" data-testid={`interest-row-${i.id.slice(0, 8)}`}>
                    <td className="px-5 py-4">
                      <span className="text-xs font-medium bg-weave-teal/10 text-weave-teal rounded-full px-3 py-1">
                        {CTA_LABELS[i.type] || i.type}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-medium text-weave-ink">{i.first_name || "—"}</p>
                      <p className="text-xs text-weave-mute">{i.email}</p>
                    </td>
                    <td className="px-5 py-4 text-weave-slate max-w-xs truncate">{i.message || "—"}</td>
                    <td className="px-5 py-4 text-weave-slate">{formatDate(i.created_at)}</td>
                    <td className="px-5 py-4">
                      <span className={`text-xs font-medium rounded-full px-3 py-1 ${i.followup_sent_at ? "bg-weave-teal/10 text-weave-teal" : "bg-weave-yellow/15 text-[#8a6a1b]"}`}>
                        {i.followup_sent_at ? "Email sent" : "Not sent"}
                      </span>
                      {i.reminder_sent_at && (
                        <span className="ml-2 text-xs font-medium rounded-full px-3 py-1 bg-weave-sand text-weave-slate">48h nudge sent</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      {i.entry_plan === "clarity" || (i.deposit_paid && !i.entry_plan) ? (
                        <select
                          value={i.clarity_outcome || "undecided"}
                          onChange={(e) => handleOutcome(i, e.target.value)}
                          data-testid={`clarity-outcome-select-${i.id.slice(0, 8)}`}
                          className="rounded-full border border-weave-line bg-white px-3 py-1.5 text-xs text-weave-slate focus:border-weave-teal focus:outline-none"
                        >
                          <option value="undecided">Still deciding</option>
                          <option value="continuing">Continuing</option>
                          <option value="declined">Declined</option>
                        </select>
                      ) : (
                        <span className="text-weave-mute">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      {(i.full_paid || (i.deposit_paid && i.balance_paid)) && (
                        <span className="mr-2 text-xs font-medium rounded-full px-3 py-1 bg-weave-teal/15 text-weave-teal">Academy paid in full</span>
                      )}
                      {!i.full_paid && i.deposit_paid && !i.balance_paid && (
                        <span className="mr-2 text-xs font-medium rounded-full px-3 py-1 bg-weave-yellow/15 text-[#8a6a1b]">{i.entry_plan === "academy" ? "Academy deposit paid" : "Personal Clarity paid"}</span>
                      )}
                      {i.monthly_active && (
                        <span className="mr-2 text-xs font-medium rounded-full px-3 py-1 bg-weave-yellow/15 text-[#8a6a1b]">Monthly plan active</span>
                      )}
                      {!i.full_paid && i.balance_paid && !i.deposit_paid && (
                        <span className="mr-2 text-xs font-medium rounded-full px-3 py-1 bg-weave-yellow/15 text-[#8a6a1b]">Balance paid</span>
                      )}
                      <button
                        onClick={() => toggleBooked(i)}
                        data-testid={`booked-toggle-${i.id.slice(0, 8)}`}
                        className={`text-xs font-medium rounded-full px-3 py-1.5 border transition-colors duration-200 ${
                          i.booked
                            ? "bg-weave-teal text-white border-weave-teal"
                            : "bg-white text-weave-slate border-weave-line hover:border-weave-teal hover:text-weave-teal"
                        }`}
                      >
                        {i.booked ? "Booked ✓" : "Mark booked"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <section className="mt-14" data-testid="payments-table-section">
          <h2 className="text-xl md:text-2xl font-medium text-weave-ink">Payments</h2>
          <div className="mt-5 bg-white border border-weave-line rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]" data-testid="payments-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-[0.12em] text-weave-mute border-b border-weave-line">
                  <th className="px-5 py-4 font-medium">Date</th>
                  <th className="px-5 py-4 font-medium">Email</th>
                  <th className="px-5 py-4 font-medium">Plan</th>
                  <th className="px-5 py-4 font-medium">Amount</th>
                  <th className="px-5 py-4 font-medium">Status</th>
                  <th className="px-5 py-4 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {(!payments || payments.length === 0) && (
                  <tr><td colSpan={6} className="px-5 py-8 text-center text-weave-mute">No payments yet.</td></tr>
                )}
                {(payments || []).map((p) => (
                  <tr key={p.session_id} className="border-b border-weave-line last:border-0 hover:bg-weave-cream/60 transition-colors duration-150" data-testid={`payment-row-${p.session_id.slice(-8)}`}>
                    <td className="px-5 py-4 text-weave-slate">{formatDate(p.created_at)}</td>
                    <td className="px-5 py-4 text-weave-slate">{p.email || "—"}</td>
                    <td className="px-5 py-4">
                      <span className="text-xs font-medium bg-weave-sand text-weave-slate rounded-full px-3 py-1">
                        {PLAN_LABELS[p.plan] || p.plan}
                      </span>
                    </td>
                    <td className="px-5 py-4 font-medium text-weave-ink">£{p.amount?.toLocaleString()}</td>
                    <td className="px-5 py-4">
                      <span className={`text-xs font-medium rounded-full px-3 py-1 ${
                        p.payment_status === "paid" ? "bg-weave-teal/10 text-weave-teal"
                        : p.payment_status === "refunded" ? "bg-weave-red/10 text-weave-red"
                        : "bg-weave-sand text-weave-mute"
                      }`}>
                        {p.payment_status}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      {p.payment_status === "paid" && (
                        <button
                          onClick={() => handleRefund(p)}
                          data-testid={`refund-button-${p.session_id.slice(-8)}`}
                          className="text-xs font-medium rounded-full px-3 py-1.5 border border-weave-red/40 text-weave-red hover:bg-weave-red/10 transition-colors duration-200"
                        >
                          Refund
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
